import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../../services/api';
import { CaretRight } from '@phosphor-icons/react';

/**
 * Sprint C — AskKindraNowCard
 * 3 dynamically relevant prompts. Reuses the existing /api/chat/starter-prompts
 * endpoint (3 fixed + ≤2 dynamic). We pick the 3 most relevant (dynamic-first,
 * then fixed) and seed the chat composer when clicked.
 *
 * Hook: navigate('/chat', { state: { seedQuery: '...' } }) — ChatPage already
 * understands this and pre-fills the input.
 */
export const AskKindraNowCard = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/chat/starter-prompts`);
        const dynamic = (res.data?.dynamic || []).map((p) => ({
          text: p.text || p,
          source: 'dynamic',
        }));
        const fixed = (res.data?.fixed || []).map((p) => ({
          text: p.text || p,
          action: p.action || null,
          source: 'fixed',
        }));
        // Dynamic prompts first (they're personalised), then fill from fixed.
        const merged = [...dynamic, ...fixed].slice(0, 3);
        if (!cancelled) setPrompts(merged);
      } catch {
        if (!cancelled) {
          // Calm fallback prompts — never leave the card empty.
          setPrompts([
            { text: 'What matters most in my health right now?', source: 'fallback' },
            { text: 'Prepare me for my next appointment', source: 'fallback' },
            { text: 'Explain my fatigue pattern', source: 'fallback' },
          ]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section data-testid="ask-kindra-now-loading" className="animate-pulse">
        <div className="h-3 w-32 bg-[#D9D2C4]/70 rounded mb-3" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-11 bg-[#ECE8E1]/60 rounded-lg" />
          ))}
        </div>
      </section>
    );
  }

  if (prompts.length === 0) return null;

  const handleClick = (p) => {
    if (p.action && p.action.startsWith('/')) {
      navigate(p.action);
      return;
    }
    navigate('/chat', { state: { seedQuery: p.text } });
  };

  return (
    <section data-testid="ask-kindra-now" aria-labelledby="ask-kindra-now-heading">
      <h3
        id="ask-kindra-now-heading"
        className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 mb-3 px-1"
      >
        Ask Kindra now
      </h3>
      <ul className="space-y-2">
        {prompts.map((p, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => handleClick(p)}
              data-testid={`ask-kindra-prompt-${i}`}
              className="group w-full text-left bg-white border border-[#E5DFD0] rounded-xl px-4 py-3 flex items-center justify-between gap-3 hover:border-[#235347]/40 hover:bg-[#F4EFE3]/40 transition-colors"
            >
              <span className="text-[13.5px] text-[#0E1C2E] leading-snug">
                {p.text}
              </span>
              <CaretRight
                size={14}
                className="text-slate-300 flex-shrink-0 transition-transform group-hover:translate-x-0.5"
              />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default AskKindraNowCard;
